import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Typography, Tag, message, Spin } from 'antd';
import {
    DesktopOutlined,
    MobileOutlined,
    LaptopOutlined,
    LogoutOutlined,
    ArrowLeftOutlined,
    ExclamationCircleFilled,
    SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../../actions/authActions';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;

interface DeviceSession {
    sessionId: string;
    deviceName: string;
    browser?: string;
    os?: string;
    isMobile?: boolean;
    ipAddress?: string;
    loggedInAt?: string;
    lastActiveAt?: string;
}

const DeviceRestriction: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const stateData = (location.state as any) || {};
    const { mobileNumber, otp,
        selectionToken: stateToken, deviceLimit: stateLimit = 2, sessions: stateSessions = [],
        redirectTo, plan, billing,
    } = stateData;

    const authlogin = useSelector((state: any) => state.otpverifyReducer);
    const revokeState = useSelector((state: any) => state.revokeDeviceSessionReducer);

    const selectionToken = stateToken || authlogin?.selectionToken;
    const deviceLimit = stateLimit || authlogin?.deviceLimit || 2;
    const [sessions, setSessions] = useState<DeviceSession[]>(
        stateSessions.length > 0 ? stateSessions : authlogin?.sessions || []
    );

    const [revokingId, setRevokingId] = useState<string | null>(null);
    const [isContinuingLogin, setIsContinuingLogin] = useState(false);
    const hasTriggeredVerifyRef = useRef(false);

    useEffect(() => {
        if (!mobileNumber || !otp || !selectionToken) {
            navigate('/login', { replace: true, state: null });
        }
    }, [mobileNumber, otp, selectionToken, navigate]);

    const handleRevoke = (sessionId: string) => {
        if (!selectionToken) {
            message.error('Session token expired. Please log in again.');
            navigate('/login');
            return;
        }
        setRevokingId(sessionId);
        hasTriggeredVerifyRef.current = false;
        dispatch(authActions.revokeDeviceSessionRequest({ selectionToken, sessionId, }));
    };

    useEffect(() => {
        if (revokeState?.isSuccess && !hasTriggeredVerifyRef.current) {
            hasTriggeredVerifyRef.current = true;
            setRevokingId(null);

            const data = revokeState?.data?.data || revokeState?.data;

            // slot still full — refresh the list, stay on this screen
            if (data?.deviceLimitReached) {
                setSessions(data.sessions || []);
                hasTriggeredVerifyRef.current = false;
                return;
            }

            if (data?.authToken) {
                setIsContinuingLogin(true);
                localStorage.setItem('AUTHDATA', JSON.stringify(data));
                localStorage.setItem('SESSIONID', data.sessionId || '');
                window.dispatchEvent(new Event('authStateChanged'));
                dispatch(authActions.clearDeviceLimit());
                sessionStorage.removeItem('OTP_ACTIVE_MOBILE');

                if (redirectTo === 'pricing' && plan && billing) {
                    navigate(`/pricing?plan=${plan}&billing=${billing}`, { replace: true });
                } else {
                    navigate('/dashboard', { replace: true });
                }
                return;
            }
        }

        if (revokeState?.isError) {
            setRevokingId(null);
            setIsContinuingLogin(false);
            hasTriggeredVerifyRef.current = false;

            const err = revokeState?.error;
            const msg = typeof err === 'string' ? err : (err?.message || err?.errorMessage);

            message.error(msg || 'Session expired. Please log in again.');
            dispatch(authActions.clearDeviceLimit());
            sessionStorage.removeItem('OTP_ACTIVE_MOBILE');
            navigate('/login', { replace: true });
        }
    }, [revokeState, dispatch, navigate, redirectTo, plan, billing]);

    // Handle successful login redirect to dashboard
    useEffect(() => {
        if (authlogin?.isSuccess && authlogin?.authData) {
            const authData = authlogin.authData?.data || authlogin.authData;
            localStorage.setItem('AUTHDATA', JSON.stringify(authData));
            window.dispatchEvent(new Event('authStateChanged'));

            if (redirectTo === 'pricing' && plan && billing) {
                navigate(`/pricing?plan=${plan}&billing=${billing}`, { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [authlogin?.isSuccess, authlogin?.authData, navigate, redirectTo, plan, billing]);

    const OnContinueLogin = useCallback(() => {
        dispatch(authActions.clearDeviceLimit());
        sessionStorage.removeItem('OTP_ACTIVE_MOBILE');
        navigate('/login', { replace: true, state: null });
    }, [dispatch, navigate]);

    const getDeviceIcon = (session: DeviceSession) => {
        if (session.isMobile) return <MobileOutlined style={{ fontSize: 22, color: 'var(--primary-color, #3689e9)' }} />;
        if (session.os?.toLowerCase().includes('mac') || session.deviceName?.toLowerCase().includes('laptop')) {
            return <LaptopOutlined style={{ fontSize: 22, color: 'var(--primary-color, #3689e9)' }} />;
        }
        return <DesktopOutlined style={{ fontSize: 22, color: 'var(--primary-color, #3689e9)' }} />;
    };

    const formatLastUsed = (lastActiveAt?: string) => {
        if (!lastActiveAt) return 'Active recently';
        const d = dayjs(lastActiveAt);
        const now = dayjs();
        if (now.diff(d, 'day') === 0) {
            return `Today, ${d.format('hh:mm A')}`;
        }
        if (now.diff(d, 'day') === 1) {
            return `Yesterday, ${d.format('hh:mm A')}`;
        }
        return d.format('DD MMM YYYY, hh:mm A');
    };

    return (
        <div className="device-restriction-page">
            <div className="device-restriction-card">
                <div className="device-restriction-left">
                    <div>
                        <Button type="text" icon={<ArrowLeftOutlined />} onClick={OnContinueLogin}
                            className="device-back-btn" >
                            Back to Login
                        </Button>

                        <div className="device-limit-badge">
                            <ExclamationCircleFilled className="device-limit-badge-icon" />
                            <span className="device-limit-badge-text">
                                LIMIT REACHED ({deviceLimit}/{deviceLimit} DEVICES)
                            </span>
                        </div>

                        <Title level={2} className="device-title">
                            Login Pending
                        </Title>
                        <Paragraph className="device-description">
                            You are currently signed in on all {deviceLimit} allowed devices. To start using driveMynt on this screen, select an active device to log out.
                        </Paragraph>
                    </div>

                    {/* Connected Devices Illustration */}
                    <div className="device-illustration-wrapper">
                        <div className="device-illustration-node">
                            <div className="device-icon-box">
                                <MobileOutlined style={{ fontSize: 24, color: 'var(--primary-color, #3689e9)' }} />
                            </div>
                            <span className="device-node-label">Phone</span>
                        </div>

                        <div className="device-illustration-connector" />

                        <div className="device-illustration-node device-node-current">
                            <div className="device-icon-box-current">
                                <DesktopOutlined style={{ fontSize: 28, color: '#ffffff' }} />
                            </div>
                            <Tag color="error" className="device-node-max-tag">Max</Tag>
                            <span className="device-node-label-current">This Device</span>
                        </div>

                        <div className="device-illustration-connector" />

                        <div className="device-illustration-node">
                            <div className="device-icon-box">
                                <LaptopOutlined style={{ fontSize: 24, color: 'var(--primary-color, #3689e9)' }} />
                            </div>
                            <span className="device-node-label">Laptop / PC</span>
                        </div>
                    </div>

                    <div className="device-security-footer">
                        <SafetyCertificateOutlined className="device-security-icon" />
                        <span>Secure multi-device session management</span>
                    </div>
                </div>

                {/* Right Section: Device List */}
                <div className="device-restriction-right">
                    <div>
                        <Title level={3} className="device-right-title">
                            Log Out 1 Device to Continue
                        </Title>
                        <Paragraph className="device-right-subtitle">
                            Select one of your active devices below to revoke access and instantly proceed on this screen.
                        </Paragraph>

                        {isContinuingLogin && (
                            <div className="device-authorizing-alert">
                                <Spin size="small" />
                                <span className="device-authorizing-text">Authorizing this device and redirecting...</span>
                            </div>
                        )}

                        <div className="device-sessions-list">
                            {sessions.map((item) => (
                                <div key={item.sessionId} className="device-session-item">
                                    <div className="device-session-left-meta">
                                        <div className="device-session-avatar">
                                            {getDeviceIcon(item)}
                                        </div>
                                        <div>
                                            <div className="device-session-title-wrap">
                                                <Text strong className="device-session-name">
                                                    {item.deviceName || (item.isMobile ? 'Mobile App' : 'Web Browser')}
                                                </Text>
                                                <Tag className="device-type-tag">
                                                    {item.isMobile ? 'Mobile' : 'Web / PC'}
                                                </Tag>
                                            </div>
                                            <div className="device-session-time">
                                                Last active: {formatLastUsed(item.lastActiveAt)}
                                            </div>
                                        </div>
                                    </div>

                                    <Button
                                        danger
                                        type="primary"
                                        icon={<LogoutOutlined />}
                                        loading={revokingId === item.sessionId}
                                        disabled={isContinuingLogin || (revokingId !== null && revokingId !== item.sessionId)}
                                        onClick={() => handleRevoke(item.sessionId)}
                                        className="device-logout-action-btn"
                                    >
                                        Log Out
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="device-cancel-footer">
                        <Button
                            type="link"
                            onClick={OnContinueLogin}
                            className="device-cancel-btn"
                        >
                            Cancel & Return to Login
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeviceRestriction;